import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Lock } from "lucide-react";
import { ADMIN_API, useAdminAuth } from "./AdminAuth";

const formatApiError = (detail) => {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  return String(detail);
};

const AdminLogin = () => {
  const navigate = useNavigate();
  const { setAdmin } = useAdminAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${ADMIN_API}/auth/login`, form, { withCredentials: true });
      setAdmin(data);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-line bg-panel px-4 py-3.5 text-sm text-cream placeholder:text-ash outline-none transition-colors focus:border-brand";

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-4">
      <div className="absolute inset-0 bg-blueprint opacity-50" aria-hidden="true" />
      <div className="relative w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <span className="inline-flex rounded-lg bg-white px-4 py-2 shadow-sm">
            <img src="/assets/logo-light.png" alt="OMNIBÂT" className="h-10 w-auto" />
          </span>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-brand">Back-office</p>
        </div>
        <form onSubmit={submit} data-testid="admin-login-form" className="border border-line bg-surface p-8">
          <div className="mb-6 flex items-center gap-3">
            <Lock className="h-5 w-5 text-brand" />
            <h1 className="font-display text-2xl font-bold uppercase tracking-wide text-cream">Connexion admin</h1>
          </div>
          {error && (
            <p data-testid="admin-login-error" className="mb-5 border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400">
              {error}
            </p>
          )}
          <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-fog">Email</label>
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="admin@omnibat.pro"
            className={inputCls}
            data-testid="admin-login-email"
          />
          <label className="mb-2 mt-5 block font-mono text-[11px] uppercase tracking-[0.2em] text-fog">Mot de passe</label>
          <input
            required
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="••••••••••"
            className={inputCls}
            data-testid="admin-login-password"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="mt-7 flex w-full items-center justify-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-[0.12em] text-ink transition-colors hover:bg-brand-hover disabled:opacity-60"
          >
            {loading && <Loader2 className="h-5 w-5 animate-spin" />}
            Se connecter
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
